import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { X, Plus, Trash2, Save, Tag, AlertCircle, Check } from 'lucide-react'

export default function PreciosCliente({ cliente, onClose }) {
  const [productos, setProductos] = useState([])
  const [preciosEspeciales, setPreciosEspeciales] = useState([])
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [modificado, setModificado] = useState(false)

  useEffect(() => {
    cargarDatos()
  }, [])

  async function cargarDatos() {
    setLoading(true)
    try {
      const [productosRes, preciosRes] = await Promise.all([
        supabase.from('products').select('id, nombre, precio_base, iva_pct').eq('activo', true).order('nombre'),
        supabase.from('client_prices').select('*').eq('client_id', cliente.id).eq('activo', true)
      ])

      const prods = productosRes.data || []
      const precios = preciosRes.data || []

      // Construir lista completa: todos los productos, marcando cuáles tienen precio especial
      const lista = prods.map(p => {
        const especial = precios.find(cp => cp.product_id === p.id)
        return {
          product_id: p.id,
          nombre: p.nombre,
          precio_base: Number(p.precio_base),
          iva_pct: Number(p.iva_pct),
          pvp: especial ? Number(especial.pvp) : null,
          precio_id: especial?.id || null,
          tiene_especial: !!especial,
          activo: !!especial
        }
      })

      setProductos(prods)
      setPreciosEspeciales(lista)
    } catch (error) {
      alert('Error cargando datos: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  function toggleEspecial(product_id) {
    setPreciosEspeciales(prev => prev.map(item => {
      if (item.product_id !== product_id) return item
      return {
        ...item,
        activo: !item.activo,
        pvp: !item.activo ? item.precio_base : item.pvp
      }
    }))
    setModificado(true)
  }

  function actualizarPrecio(product_id, valor) {
    setPreciosEspeciales(prev => prev.map(item => {
      if (item.product_id !== product_id) return item
      return { ...item, pvp: valor }
    }))
    setModificado(true)
  }

  async function guardar() {
    setGuardando(true)
    try {
      const conEspecial = preciosEspeciales.filter(i => i.activo && i.pvp !== null)
      const sinEspecial = preciosEspeciales.filter(i => !i.activo && i.precio_id)

      // Eliminar los que ya no tienen precio especial
      if (sinEspecial.length > 0) {
        await supabase
          .from('client_prices')
          .delete()
          .in('id', sinEspecial.map(i => i.precio_id))
      }

      // Upsert los que tienen precio especial
      for (const item of conEspecial) {
        const pvp = Number(item.pvp)
        if (isNaN(pvp) || pvp <= 0) continue

        if (item.precio_id) {
          // Actualizar existente
          await supabase
            .from('client_prices')
            .update({ pvp, updated_at: new Date().toISOString() })
            .eq('id', item.precio_id)
        } else {
          // Insertar nuevo
          await supabase
            .from('client_prices')
            .insert({
              client_id: cliente.id,
              product_id: item.product_id,
              pvp,
              activo: true,
              notas: 'Editado desde app'
            })
        }
      }

      setModificado(false)
      await cargarDatos()
      alert(`✅ Precios guardados para ${cliente.alias || cliente.razon_social}`)
    } catch (error) {
      alert('Error guardando: ' + error.message)
    } finally {
      setGuardando(false)
    }
  }

  const conEspecial = preciosEspeciales.filter(i => i.activo)
  const sinEspecial = preciosEspeciales.filter(i => !i.activo)

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* CABECERA */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'rgba(var(--accent-rgb, 74,222,128), 0.15)',
              borderRadius: '8px', padding: '0.5rem'
            }}>
              <Tag size={20} color='var(--accent)' />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                Precios especiales
              </h2>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--muted)' }}>
                {cliente.alias || cliente.razon_social}
                {conEspecial.length > 0 && (
                  <span style={{
                    marginLeft: '0.5rem',
                    background: 'rgba(var(--accent-rgb,74,222,128),0.15)',
                    color: 'var(--accent)',
                    borderRadius: '4px',
                    padding: '0.1rem 0.4rem',
                    fontSize: '0.75rem'
                  }}>
                    {conEspecial.length} precio{conEspecial.length !== 1 ? 's' : ''} especial{conEspecial.length !== 1 ? 'es' : ''}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '0.25rem' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* AVISO */}
        <div style={{
          margin: '1rem 1.5rem 0',
          padding: '0.75rem 1rem',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: '8px',
          display: 'flex', gap: '0.5rem', alignItems: 'flex-start',
          fontSize: '0.8rem', color: '#f59e0b'
        }}>
          <AlertCircle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
          <span>
            Activa el interruptor de un producto para fijar un precio diferente al base.
            El precio especial se usará en todos los albaranes de este cliente.
          </span>
        </div>

        {/* CONTENIDO */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
              Cargando productos...
            </div>
          ) : (
            <>
              {/* PRODUCTOS CON PRECIO ESPECIAL */}
              {conEspecial.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em',
                    color: 'var(--accent)', textTransform: 'uppercase', marginBottom: '0.5rem'
                  }}>
                    Con precio especial
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {conEspecial.map(item => (
                      <FilaProducto
                        key={item.product_id}
                        item={item}
                        onToggle={toggleEspecial}
                        onCambiarPrecio={actualizarPrecio}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* SEPARADOR */}
              {conEspecial.length > 0 && sinEspecial.length > 0 && (
                <div style={{
                  borderTop: '1px solid var(--line)',
                  marginBottom: '1.5rem'
                }} />
              )}

              {/* PRODUCTOS SIN PRECIO ESPECIAL */}
              {sinEspecial.length > 0 && (
                <div>
                  <div style={{
                    fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em',
                    color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.5rem'
                  }}>
                    Precio base — pulsa + para añadir precio especial
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {sinEspecial.map(item => (
                      <FilaProducto
                        key={item.product_id}
                        item={item}
                        onToggle={toggleEspecial}
                        onCambiarPrecio={actualizarPrecio}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* PIE */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--line)',
          display: 'flex', gap: '0.75rem', justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.6rem 1.2rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--line)',
              borderRadius: '8px',
              color: 'var(--fg)',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={!modificado || guardando}
            style={{
              padding: '0.6rem 1.4rem',
              background: modificado ? 'var(--accent)' : 'var(--bg-secondary)',
              border: 'none',
              borderRadius: '8px',
              color: modificado ? '#000' : 'var(--muted)',
              cursor: modificado ? 'pointer' : 'not-allowed',
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              transition: 'all 0.2s'
            }}
          >
            <Save size={16} />
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

function FilaProducto({ item, onToggle, onCambiarPrecio }) {
  const diferencia = item.activo && item.pvp !== null
    ? Number(item.pvp) - item.precio_base
    : 0

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.6rem 0.9rem',
      background: item.activo ? 'rgba(var(--accent-rgb,74,222,128),0.06)' : 'var(--bg-secondary)',
      border: `1px solid ${item.activo ? 'rgba(var(--accent-rgb,74,222,128),0.25)' : 'var(--line)'}`,
      borderRadius: '8px',
      transition: 'all 0.15s'
    }}>

      {/* Toggle */}
      <button
        onClick={() => onToggle(item.product_id)}
        title={item.activo ? 'Quitar precio especial' : 'Añadir precio especial'}
        style={{
          width: '28px', height: '28px',
          borderRadius: '6px',
          border: 'none',
          background: item.activo ? 'var(--accent)' : 'var(--bg)',
          color: item.activo ? '#000' : 'var(--muted)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.15s'
        }}
      >
        {item.activo ? <Check size={15} strokeWidth={3} /> : <Plus size={15} />}
      </button>

      {/* Nombre */}
      <span style={{
        flex: 1,
        fontSize: '0.9rem',
        fontWeight: item.activo ? 600 : 400,
        color: item.activo ? 'var(--fg)' : 'var(--muted)'
      }}>
        {item.nombre}
      </span>

      {/* Precio base (siempre visible) */}
      <span style={{
        fontSize: '0.8rem',
        color: 'var(--muted)',
        minWidth: '70px',
        textAlign: 'right',
        textDecoration: item.activo ? 'line-through' : 'none'
      }}>
        {item.precio_base.toFixed(2)}€
      </span>

      {/* Input precio especial — solo si activo */}
      {item.activo ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={item.pvp ?? ''}
            onChange={e => onCambiarPrecio(item.product_id, e.target.value)}
            style={{
              width: '80px',
              padding: '0.35rem 0.5rem',
              background: 'var(--bg)',
              border: '1px solid var(--accent)',
              borderRadius: '6px',
              color: 'var(--fg)',
              fontSize: '0.9rem',
              fontWeight: 700,
              textAlign: 'right'
            }}
          />
          <span style={{ fontSize: '0.85rem', color: 'var(--fg)' }}>€</span>
          {/* Indicador diferencia */}
          {diferencia !== 0 && (
            <span style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              color: diferencia > 0 ? '#10b981' : '#f59e0b',
              minWidth: '42px',
              textAlign: 'right'
            }}>
              {diferencia > 0 ? '+' : ''}{diferencia.toFixed(2)}
            </span>
          )}
        </div>
      ) : (
        <div style={{ width: '120px' }} />
      )}
    </div>
  )
}
